import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ServiceExecutionState, RequestRevisionRequest } from "./models/executionModels";
import serviceExecutionApi from "./api/serviceExecutionApi";

export const fetchProgressUpdates = createAsyncThunk(
  "serviceExecutionSlice/fetchProgressUpdates",
  async (serviceRequestId: string) => {
    return serviceExecutionApi.getProgressUpdates(serviceRequestId);
  }
);

export const fetchCompletion = createAsyncThunk(
  "serviceExecutionSlice/fetchCompletion",
  async (serviceRequestId: string) => {
    return serviceExecutionApi.getCompletion(serviceRequestId);
  }
);

export const confirmCompletion = createAsyncThunk(
  "serviceExecutionSlice/confirmCompletion",
  async (serviceRequestId: string) => {
    return serviceExecutionApi.confirmCompletion(serviceRequestId);
  }
);

export const requestRevision = createAsyncThunk(
  "serviceExecutionSlice/requestRevision",
  async ({ serviceRequestId, data }: { serviceRequestId: string; data: RequestRevisionRequest }) => {
    return serviceExecutionApi.requestRevision(serviceRequestId, data);
  }
);

const initialState: ServiceExecutionState = {
  progressUpdates: [],
  completion: null,
  completionLoading: false,
  loading: false,
  error: undefined,
};

const serviceExecutionSlice = createSlice({
  name: "serviceExecutionSlice",
  initialState,
  reducers: {
    clearProgressUpdates: (state) => {
      state.progressUpdates = [];
    },
    clearCompletion: (state) => {
      state.completion = null;
    },
    clearError: (state) => {
      state.error = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchProgressUpdates.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchProgressUpdates.fulfilled, (state, { payload }) => {
        state.progressUpdates = payload;
        state.loading = false;
      })
      .addCase(fetchProgressUpdates.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // fetchCompletion
      .addCase(fetchCompletion.pending, (state) => {
        state.completionLoading = true;
      })
      .addCase(fetchCompletion.fulfilled, (state, { payload }) => {
        state.completion = payload;
        state.completionLoading = false;
      })
      .addCase(fetchCompletion.rejected, (state) => {
        state.completion = null;
        state.completionLoading = false;
      })
      // confirmCompletion
      .addCase(confirmCompletion.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(confirmCompletion.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(confirmCompletion.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      })
      // requestRevision
      .addCase(requestRevision.pending, (state) => {
        state.loading = true;
        state.error = undefined;
      })
      .addCase(requestRevision.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(requestRevision.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });
  },
});

export const { clearProgressUpdates, clearCompletion, clearError } = serviceExecutionSlice.actions;

export default serviceExecutionSlice.reducer;
